export const LANGUAGE_FIELDS = {
  cultures: {
    fields: {
      fr: 'french_name',
      en: 'english_name',
      lat: 'latin_name',
    },
    labels: {
      fr: 'Français',
      en: 'Anglais',
      lat: 'Latin',
    },
    defaultLanguage: 'fr',
  },
  enemies: {
    fields: {
      fr: 'display_french_latin',
      en: 'display_english_latin',
      lat: 'display_latin_french',
    },
    labels: {
      fr: 'Français / Latin',
      en: 'Anglais / Latin',
      lat: 'Latin / Français',
    },
    defaultLanguage: 'fr',
  },
};

// Formats a product-name option as "Name (packaging1, packaging2 — reg)"
// using the data the backend attaches via include_source_fields.
const formatProductNameLabel = (option, source) => {
  const parts = [];

  const pkgs = source?.packagings;
  const pkgList = Array.isArray(pkgs)
    ? pkgs.map((p) => (typeof p === 'object' ? p?.name : p)).filter(Boolean)
    : [];
  if (pkgList.length > 0) parts.push(pkgList.join(', '));

  const reg = source?.approval_number;
  if (reg) parts.push(String(reg));

  if (parts.length === 0) return option.label;
  return `${option.label} (${parts.join(' — ')})`;
};

export const BASIC_FILTER_CONFIGS = [
  {
    key: 'product_names',
    labelKey: 'product|filters.Product name',
    field: 'name.raw',
    searchFields: [
      { field: 'name.keyword', primary: true },
      {
        field: 'packagings.name.keyword',
        label: 'Emballage',
        sourceField: 'packagings',
      },
      {
        field: 'approval_number.keyword',
        label: "N° d'homologation",
        sourceField: 'approval_number',
      },
    ],
    includeSourceFields: ['packagings', 'approval_number'],
    formatOptionLabel: formatProductNameLabel,
  },
  {
    key: 'active_substance_names',
    labelKey: 'product|filters.Active substance name',
    field: 'active_substances.active_substance.name.raw',
    // Backend flattens the nested paths into parallel arrays via
    // _navigate_path, so the frontend can treat them like the substance
    // page (clean, flat source fields).
    searchFields: [
      { field: 'active_substances.active_substance.name.keyword', primary: true },
      {
        field: 'active_substances.active_substance.cas_numbers.keyword',
        label: 'CAS',
        sourceField: 'active_substances.active_substance.cas_numbers',
      },
      {
        field: 'active_substances.active_substance.synonyms.keyword',
        label: 'Synonyme',
        sourceField: 'active_substances.active_substance.synonyms',
      },
    ],
    includeSourceFields: [
      'active_substances.active_substance.cas_numbers',
      'active_substances.active_substance.synonyms',
    ],
    formatOptionLabel: (option, source) => {
      const cas = source?.['active_substances.active_substance.cas_numbers'];
      const casList = Array.isArray(cas)
        ? cas.filter((v) => v != null && String(v).trim() !== '')
        : cas
          ? [cas]
          : [];
      if (casList.length === 0) return option.label;
      return `${option.label} (${casList.join(', ')})`;
    },
  },
  {
    key: 'activity_sectors',
    labelKey: 'product|filters.Activity sector',
    field: 'activities_sector',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'organic_approval',
    labelKey: 'product|filters.Organic approval',
    field: 'is_organic_approval',
    type: 'boolean_select',
  },
  {
    key: 'cultures',
    labelKey: 'product|filters.Culture (from treatments)',
    field: 'cultures',
    value_field: 'id',
    label_field: 'french_name',
    languageConfig: LANGUAGE_FIELDS.cultures,
    filters: { filter_hide_in_pc_ma_search: false, filter_show_in_customer_filter_filter_PC: true },
  },
  {
    key: 'cultures_practices',
    labelKey: 'product|filters.Cultural practices',
    field: 'cultures.cultural_practices',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'enemies',
    labelKey: 'product|filters.Enemies',
    field: 'enemies',
    value_field: 'id',
    label_field: 'client_display_name',
    languageConfig: LANGUAGE_FIELDS.enemies,
    infoBulle: {
      title: 'product|filters.enemiesDisplayMode.title',
      content: 'product|filters.enemiesDisplayMode.content',
    },
    tutorialSlotKey: 'product-search-enemies',
  },
];

export const AGRONOMIC_FILTER_CONFIGS = [
  {
    key: 'chemical_families',
    labelKey: 'product|filters.Chemical families or biological families',
    field: 'pesticide_group_associations.chemical_family',
    value_field: 'id',
    label_field: 'label',
  },
  {
    key: 'pesticide_groups',
    labelKey: 'product|filters.Pesticide groups',
    field: 'pesticide_group_associations.pesticide_group',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'pesticide_categories',
    labelKey: 'product|filters.Pesticide categories',
    field: 'active_substances.active_substance.pesticide_categories',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'arla_culture_groups',
    labelKey: 'product|filters.ARLA Culture Groups',
    field: 'cultures.arla_culture_group',
    value_field: 'id',
    label_field: 'group',
  },
  {
    key: 'product_types',
    labelKey: 'product|filters.Product types',
    field: 'product_types',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'activity_types',
    labelKey: 'product|filters.Activity types',
    field: 'activity_type_filters',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'application_types',
    labelKey: 'product|filters.Application Types',
    field: 'application_types',
    value_field: 'id',
    label_field: 'name',
  },
  {
    key: 'allergens',
    labelKey: 'product|filters.Allergens',
    field: 'allergens',
    value_field: 'id',
    label_field: 'name',
  },
];

export const HEALTH_FILTER_CONFIGS = [
  {
    key: 'acute_toxicity',
    labelKey: 'product|filters.Acute toxicity',
    field: 'symbols.health.acute_toxicity_symbol.calculated_value',
  },
  {
    key: 'long_term_effects',
    labelKey: 'product|filters.Long term effects',
    field: 'symbols.health.long_term_effect_symbol.calculated_value',
  },
];

export const ENVIRONMENT_FILTER_CONFIGS = [
  {
    key: 'fish_or_daphnia',
    labelKey: 'product|filters.Fish or daphnia',
    field: 'symbols.environment.fishes_symbol.calculated_value',
  },
  {
    key: 'birds',
    labelKey: 'product|filters.Birds',
    field: 'symbols.environment.birds_symbol.calculated_value',
  },
  {
    key: 'bees',
    labelKey: 'product|filters.Bees',
    field: 'symbols.environment.bees_symbol.calculated_value',
  },
  {
    key: 'persistence',
    labelKey: 'product|filters.Persistence',
    field: 'symbols.environment.persistence_symbol.calculated_value',
  },
  {
    key: 'leaching_potential',
    labelKey: 'product|filters.Leaching potential',
    field: 'symbols.environment.leaching_symbol.calculated_value',
  },
];

export const BASIC_FILTER_KEYS = BASIC_FILTER_CONFIGS.map((c) => c.key);
export const EXTRA_FILTER_KEYS = [
  ...AGRONOMIC_FILTER_CONFIGS.map((c) => c.key),
  ...HEALTH_FILTER_CONFIGS.map((c) => c.key),
  ...ENVIRONMENT_FILTER_CONFIGS.map((c) => c.key),
];

// Union of every configurable filter — used by FilterSection to compute the
// cross-field facet context passed to each ElasticsearchSelect.
export const ALL_FILTER_CONFIGS = [
  ...BASIC_FILTER_CONFIGS,
  ...AGRONOMIC_FILTER_CONFIGS,
  ...HEALTH_FILTER_CONFIGS,
  ...ENVIRONMENT_FILTER_CONFIGS,
];

export const DOCUMENT_TYPE = 'ProductDocument';